/**
 * The glass: what a typed string becomes before it reaches the store, and what a stored number
 * looks like in the cell it came from.
 *
 * Every rule here exists because the wire's unit and the trainer's unit are not the same unit:
 *
 * · **Percent is a 0–1 FRACTION on the wire and a percentage in the cell.** `PostSetInput`
 *   `PrescribedPercent` carries `[Range(0.0, 1.0)]`; the canon prints "87,5 % 1RM". Scaling at the
 *   glass is the only place the two can be reconciled without either the store or the payload
 *   learning a display convention.
 * · **Rest is seconds on the wire and `m:ss` in the cell.** The canon prints "2:30", and a trainer
 *   who types `150` means the same thing. Both are accepted; the cell prints the canon's form back.
 * · **A cleared rest cell is not an empty rest.** Under the seed semantics every set always carries
 *   a concrete rest, so a backspace commits the DAY's value rather than `null` — `restCellSeconds`
 *   is the entity's rule and this module only routes to it.
 *
 * ── Clamping happens HERE, at the input, and never only at the payload ───────────────────────
 * Document-model ADR §10: the client validates so a save rarely fails against a rule the UI already
 * enforced. A trainer who types 3000 kg sees the cell settle on 2000 — the dictionary's ceiling —
 * rather than a 400 arriving after a save that also carried the rest of their plan.
 *
 * ── The decimal separator is the trainer's, not the platform's ───────────────────────────────
 * A Polish keyboard's numeric comma is what a trainer types for 87,5, and `Number('87,5')` is
 * `NaN`. Both separators are accepted on the way in; the way out is `Intl`'s, so what is typed and
 * what is printed agree in either language.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import {
  PERCENT_RANGE,
  REST_SECONDS_RANGE,
  restCellSeconds,
  type SetFieldPatch,
  type SetValueKey,
} from '~/entities/plan-draft';
import { clockDuration } from '~/shared/lib/clockDuration';
import { clampMeasurementValue, type MeasurementFieldKey } from '~/shared/measurement';

/** Everything a cell needs to know to turn its own text into a patch. */
export interface CellContract {
  /** The `SetDraft` key this cell writes. */
  readonly valueKey: SetValueKey;
  /**
   * The measurement field behind the cell, or `null` for REST — which is not a measurement field
   * and carries its own range (`PostSetInput.PrescribedRestTime`) rather than a dictionary entry.
   */
  readonly field: MeasurementFieldKey | null;
  /**
   * True when the cell shows a PERCENTAGE over a 0–1 wire fraction — a %1RM prescription or a
   * ladder member's fraction of the top. Independent of `field`, because both ride the weight
   * column and only the exercise's mode and the set's type can tell them apart.
   */
  readonly isPercent: boolean;
  /** True for the rest cell, whose empty value means "follow the day" rather than "no value". */
  readonly isRest: boolean;
}

/**
 * A typed string → a number, or `null` for "nothing here".
 *
 * Anything that is not a finite number is `null` rather than `0`: a half-typed "-" or a stray letter
 * must not commit a real prescription of zero, which the contract accepts as legitimate (0 kg is a
 * bodyweight movement, RIR 0 is "to failure") and which nobody could tell from a typo afterwards.
 */
export function parseCellNumber(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed.length === 0) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * A rest cell's text → seconds.
 *
 * Two accepted forms and one deliberate refusal:
 * · `150` — plain seconds, what a trainer used to the wire types.
 * · `2:30` — the canon's own form, and what the cell prints back.
 * · `2:75` is **not** 3:15. A minutes field carrying more than 59 seconds is a typo, not a
 *   notation, and silently normalising it would commit a rest the trainer never wrote.
 */
export function parseRestCell(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;

  const clock = /^(\d+):([0-5]\d)$/.exec(trimmed);
  if (clock !== null) {
    const minutes = Number(clock[1]);
    const seconds = Number(clock[2]);
    return minutes * 60 + seconds;
  }
  return parseCellNumber(trimmed);
}

/**
 * Bound a value into what the contract accepts for this cell.
 *
 * The three ranges come from three different authorities and none of them is restated here: the
 * dictionary for a measurement field, `REST_SECONDS_RANGE` for rest, `PERCENT_RANGE` for a fraction.
 * A percentage is clamped in FRACTION space, after scaling, so 0–100 at the glass and 0–1 on the
 * wire cannot disagree about where the ceiling is.
 */
function clampForCell(contract: CellContract, value: number): number {
  if (contract.isPercent) {
    return Math.min(PERCENT_RANGE.max, Math.max(PERCENT_RANGE.min, value));
  }
  if (contract.isRest) {
    const whole = Math.round(value);
    return Math.min(REST_SECONDS_RANGE.max, Math.max(REST_SECONDS_RANGE.min, whole));
  }
  return contract.field === null ? value : clampMeasurementValue(contract.field, value);
}

/**
 * One cell's text → the patch the store should apply. Always a patch, never a decision to skip:
 * clearing a cell is an edit, and the store's own no-op guard is what stops an unchanged value from
 * marking the plan unsaved.
 *
 * @param dayRestSeconds The day's concrete rest, for the cleared-rest rule. Ignored by every other
 *   cell, and passed rather than looked up because `model/` reaches nothing.
 */
export function commitSetCell(
  contract: CellContract,
  raw: string,
  dayRestSeconds: number,
): SetFieldPatch {
  if (contract.isRest) {
    const parsed = parseRestCell(raw);
    const seconds = restCellSeconds(parsed === null ? null : clampForCell(contract, parsed), dayRestSeconds);
    return { [contract.valueKey]: seconds };
  }

  const parsed = parseCellNumber(raw);
  if (parsed === null) return { [contract.valueKey]: null };

  // The percentage the trainer typed is a hundred times the fraction the wire wants. Scaled BEFORE
  // clamping, so the bound applies to the value that ships.
  const scaled = contract.isPercent ? parsed / 100 : parsed;
  return { [contract.valueKey]: clampForCell(contract, scaled) };
}

/**
 * A stored value → the text a cell shows.
 *
 * `Intl` rather than `toFixed`, so a Polish trainer reads "87,5" and an English one "87.5" — the
 * same separator each of them types. `maximumFractionDigits` is 2 rather than the dictionary's step
 * precision: a percentage of a percentage (a 0.875 fraction is 87.5) needs one place, and a
 * half-kilogram needs one, but a value that arrived from the phone with more precision than either
 * must not be silently rounded into a different prescription by the act of rendering it.
 */
export function formatCellNumber(
  value: number | null,
  contract: CellContract,
  locale: string,
): string {
  if (value === null) return '';
  if (contract.isRest) return clockDuration(value);
  const shown = contract.isPercent ? value * 100 : value;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(shown);
}
